import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Tabs, Tab, Badge, Collapse, ProgressBar, Table, Modal, ButtonGroup, InputGroup } from 'react-bootstrap';
import { getAdminSettings, updateAdminSettings, getSystemResources, getApplicationHealth, getPerformanceMetrics, getDebugSwitches, updateDebugSwitches, getThreads, getThreadHealth, learnThreads, controlThread } from '../services/api';

// Provider capabilities info (static, defined once at module scope)
const PROVIDER_INFO = {
  finnhub: {
    name: "Finnhub.io",
    supportsRegularTickers: true,
    supportsMarketIndexes: false,
    requiresApiKey: true,
    defaultRateLimit: 60,
    description: "Good for regular stock tickers, limited index support (premium only)",
    advantages: ["Real-time stock data", "Excellent for US stocks"],
    limitations: ["Limited market index support", "Rate limits on free tier"]
  },
  yahoo_finance: {
    name: "Yahoo Finance",
    supportsRegularTickers: true,
    supportsMarketIndexes: true,
    requiresApiKey: false,
    defaultRateLimit: 500,
    description: "Good all-around provider with market index support",
    advantages: ["Supports market indexes", "Higher rate limits", "No API key needed for basic usage"],
    limitations: ["Sometimes less accurate than Finnhub for real-time prices"]
  },
  twelvedata: {
    name: "Twelve Data",
    supportsRegularTickers: true,
    supportsMarketIndexes: true,
    requiresApiKey: true,
    defaultRateLimit: 60,
    defaultBatchSize: 55,
    description: "High-quality financial data provider with batch processing",
    advantages: ["Batch processing (55 symbols/call)", "Excellent data quality", "Global market coverage", "Supports both tickers and indexes"],
    limitations: ["Requires API key", "60 batch calls/hour limit", "1 batch per minute rate limiting"]
  }
};

function AdminSettings() {
  const [settings, setSettings] = useState({
    // Active service selection
    active_finance_service: 'twelvedata',
    ticker_data_provider: 'twelvedata',
    index_data_provider: 'twelvedata',
    
    // Global settings
    global_ticker_update_interval: 0,

    // Yahoo Finance API settings
    yahoo_finance_api_key: '',
    yahoo_finance_rate_limit: 100,
    yahoo_finance_endpoint: 'https://yfapi.net/v6/finance',
    yahoo_finance_min_update_interval: 60,
    yahoo_finance_max_requests_per_hour: 500,
    
    // Finnhub API settings
    finnhub_api_key: '',
    finnhub_min_update_interval: 60,
    finnhub_max_requests_per_hour: 60,
    
    // Twelve Data API settings
    twelvedata_api_key: '',
    twelvedata_max_requests_per_minute: 55,
    twelvedata_rate_limit_capacity: 0.8,
    twelvedata_batch_size: 55,
    
    // Market hours settings
    market_hours_start: '9:30',
    market_hours_end: '16:00',
    market_timezone: 'America/New_York',

    // Extended hours settings
    extended_hours_enabled: false,
    extended_hours_start: '4:00',
    extended_hours_end: '20:00',
    
    // Security settings
    password_expiration_days: 180,
    
    // Debug Console settings
    default_debug_log_level: 'WARNING',
  });
  
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [originalSettings, setOriginalSettings] = useState(null);
  
  // Main tab state
  const [activeTab, setActiveTab] = useState('configuration');
  
  // Collapse states for each card
  const [collapseStates, setCollapseStates] = useState({
    providerConfig: false,
    globalApiSettings: false,
    marketHours: false,
    securitySettings: false,
    twelvedata: false,
    finnhub: false,
    yahooFinance: false,
    globalDebugSettings: false
  });

  const defaultTwelvedataCapacity = 0.8;

  const twelvedataCapacityDisplayValue = useMemo(() => {
    const rawValue = settings.twelvedata_rate_limit_capacity;

    if (rawValue === '' || rawValue === null || typeof rawValue === 'undefined') {
      return '';
    }

    const capacity = Number(rawValue);
    if (!Number.isFinite(capacity)) {
      return '';
    }

    return Math.round(capacity * 1000) / 10;
  }, [settings.twelvedata_rate_limit_capacity]);

  const twelvedataEffectiveRequestsPerMinute = useMemo(() => {
    const rawLimit = Number(settings.twelvedata_max_requests_per_minute);
    const capacityValue = Number(settings.twelvedata_rate_limit_capacity);

    if (!Number.isFinite(rawLimit) || rawLimit <= 0) {
      return 0;
    }

    if (!Number.isFinite(capacityValue) || capacityValue <= 0) {
      return Math.floor(rawLimit * defaultTwelvedataCapacity);
    }

    return Math.floor(rawLimit * capacityValue);
  }, [
    settings.twelvedata_max_requests_per_minute,
    settings.twelvedata_rate_limit_capacity
  ]);

  const twelvedataEstimatedTickersPerMinute = useMemo(() => {
    if (!Number.isFinite(twelvedataEffectiveRequestsPerMinute) || twelvedataEffectiveRequestsPerMinute <= 0) {
      return 0;
    }

    return Math.floor(twelvedataEffectiveRequestsPerMinute / 2);
  }, [twelvedataEffectiveRequestsPerMinute]);

  // Ensure providers are valid
  const ensureValidProvider = useCallback((provider) => {
    return PROVIDER_INFO[provider] ? provider : Object.keys(PROVIDER_INFO)[0];
  }, []);

  // Toggle collapse state
  const toggleCollapse = (cardName) => {
    setCollapseStates(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getAdminSettings();
      const fetchedSettings = response.data;

      // Validate providers to ensure they exist in PROVIDER_INFO
      fetchedSettings.ticker_data_provider = ensureValidProvider(fetchedSettings.ticker_data_provider);
      fetchedSettings.index_data_provider = ensureValidProvider(fetchedSettings.index_data_provider);

      setSettings(fetchedSettings);
      setOriginalSettings({ ...fetchedSettings });
    } catch (err) {
      console.error('Error fetching admin settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [ensureValidProvider]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'twelvedata_rate_limit_capacity') {
      if (value === '') {
        setSettings({
          ...settings,
          [name]: ''
        });
      } else {
        const parsedValue = parseFloat(value);

        if (Number.isNaN(parsedValue)) {
          return;
        }

        const clampedPercent = Math.min(Math.max(parsedValue, 5), 100);
        const decimalCapacity = Math.round((clampedPercent / 100) * 1000) / 1000;

        setSettings({
          ...settings,
          [name]: decimalCapacity
        });
      }

      return;
    }

    // When changing ticker or index provider, update the main service if they match
    if (name === 'ticker_data_provider' && settings.active_finance_service === settings.ticker_data_provider) {
      setSettings({
        ...settings,
        [name]: value,
        active_finance_service: value
      });
    } else {
      setSettings({
        ...settings,
        [name]: value
      });
    }
  };

  const handleSectionSave = async (sectionName, sectionSettings) => {
    setSavingSection(sectionName);
    setError('');
    setSuccessMessage('');

    try {
      // Create a partial settings object with only the section's settings
      const partialSettings = {};
      sectionSettings.forEach(settingKey => {
        if (settings.hasOwnProperty(settingKey)) {
          let valueToStore = settings[settingKey];

          if (settingKey === 'twelvedata_rate_limit_capacity' && (valueToStore === '' || valueToStore === null || typeof valueToStore === 'undefined')) {
            valueToStore = defaultTwelvedataCapacity;
          }

          partialSettings[settingKey] = valueToStore;
        }
      });

      await updateAdminSettings(partialSettings);
      setSettings(prevSettings => {
        const updatedSettings = { ...prevSettings };
        Object.entries(partialSettings).forEach(([key, value]) => {
          updatedSettings[key] = value;
        });
        return updatedSettings;
      });
      setOriginalSettings(prevOriginal => {
        const updatedOriginal = { ...(prevOriginal || {}) };
        Object.entries(partialSettings).forEach(([key, value]) => {
          updatedOriginal[key] = value;
        });
        return updatedOriginal;
      });
      setSuccessMessage(`${sectionName} settings updated successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error(`Error updating ${sectionName} settings:`, err);
      setError(`Failed to update ${sectionName} settings. Please try again.`);
    } finally {
      setSavingSection('');
    }
  };

  const normalizeValueForComparison = (value) => {
    if (value === null || typeof value === 'undefined') {
      return '';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'string') {
      return value;
    }

    try {
      return JSON.stringify(value);
    } catch (err) {
      return String(value);
    }
  };

  const hasSectionChanges = (sectionKeys) => {
    if (!originalSettings) {
      return false;
    }

    return sectionKeys.some((key) => {
      const currentValue = normalizeValueForComparison(settings[key]);
      const originalValue = normalizeValueForComparison(originalSettings[key]);
      return currentValue !== originalValue;
    });
  };

  // Check if API keys are configured for the selected providers
  const missingApiKeyWarning = () => {
    const warnings = [];
    
    if (settings.ticker_data_provider === 'finnhub' && !settings.finnhub_api_key) {
      warnings.push('Finnhub API key is missing for ticker data');
    }
    
    if (settings.index_data_provider === 'finnhub' && !settings.finnhub_api_key) {
      warnings.push('Finnhub API key is missing for market index data');
    }
    
    if (settings.ticker_data_provider === 'twelvedata' && !settings.twelvedata_api_key) {
      warnings.push('Twelve Data API key is missing for ticker data');
    }
    
    if (settings.index_data_provider === 'twelvedata' && !settings.twelvedata_api_key) {
      warnings.push('Twelve Data API key is missing for market index data');
    }
    
    if (settings.ticker_data_provider === 'yahoo_finance' && 
        settings.yahoo_finance_api_key === '' && 
        settings.yahoo_finance_endpoint.includes('yfapi.net')) {
      warnings.push('Yahoo Finance API key may be needed for official API');
    }
    
    return warnings;
  };

  if (loading) {
    return (
      <Container className="mt-3">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading settings...</p>
        </div>
      </Container>
    );
  }

  const warnings = missingApiKeyWarning();

  return (
    <Container className="mt-3">
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}
      {successMessage && <Alert variant="success" className="py-2">{successMessage}</Alert>}
      
      {warnings.length > 0 && (
        <Alert variant="warning" className="py-2">
          <div className="fw-bold mb-1">Configuration Warnings:</div>
          <ul className="mb-0 ps-3">
            {warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Main Admin Tabs */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="configuration" title="Configuration">
          <ConfigurationTab
            settings={settings}
            handleInputChange={handleInputChange}
            handleSectionSave={handleSectionSave}
            savingSection={savingSection}
            collapseStates={collapseStates}
            toggleCollapse={toggleCollapse}
            providerInfo={PROVIDER_INFO}
            setSettings={setSettings}
            twelvedataEffectiveRequestsPerMinute={twelvedataEffectiveRequestsPerMinute}
            twelvedataEstimatedTickersPerMinute={twelvedataEstimatedTickersPerMinute}
            twelvedataCapacityDisplayValue={twelvedataCapacityDisplayValue}
            hasSectionChanges={hasSectionChanges}
          />
        </Tab>
        
        <Tab eventKey="system-monitor" title="System Monitor">
          <SystemMonitorTab />
        </Tab>
        
        <Tab eventKey="debug-console" title="Backend Debug">
          <DebugConsoleTab
            settings={settings}
            handleInputChange={handleInputChange}
            handleSectionSave={handleSectionSave}
            savingSection={savingSection}
            collapseStates={collapseStates}
            toggleCollapse={toggleCollapse}
            hasSectionChanges={hasSectionChanges}
          />
        </Tab>

        <Tab eventKey="frontend-console" title="Frontend Debug">
          <FrontendDebugTab />
        </Tab>

        <Tab eventKey="thread-manager" title="Thread Manager">
          <ThreadManagerTab />
        </Tab>
      </Tabs>

    </Container>
  );
}

// Configuration Tab Component
function ConfigurationTab({ settings, handleInputChange, handleSectionSave, savingSection, collapseStates, toggleCollapse, providerInfo, setSettings, twelvedataEffectiveRequestsPerMinute, twelvedataEstimatedTickersPerMinute, twelvedataCapacityDisplayValue, hasSectionChanges }) {
  return (
    <Form>
        <Row>
          {/* Left Column - Service Selection & Global Settings */}
          <Col lg={5}>
            <Card className="mb-3">
              <Card.Header 
                className="py-2 d-flex justify-content-between align-items-center" 
                style={{ cursor: 'pointer' }}
                onClick={() => toggleCollapse('providerConfig')}
              >
                <h5 className="m-0 d-flex align-items-center">
                  <i className={`bi bi-chevron-${collapseStates.providerConfig ? 'up' : 'down'} me-2`}></i>
                  Provider Configuration
                </h5>
                <Button
                  variant={
                    hasSectionChanges([
                      'ticker_data_provider',
                      'index_data_provider',
                      'active_finance_service'
                    ]) ? 'danger' : 'primary'
                  }
                  size="sm"
                  disabled={
                    savingSection === 'Provider Configuration' ||
                    !hasSectionChanges([
                      'ticker_data_provider',
                      'index_data_provider',
                      'active_finance_service'
                    ])
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionSave('Provider Configuration', [
                      'ticker_data_provider',
                      'index_data_provider',
                      'active_finance_service'
                    ]);
                  }}
                >
                  {savingSection === 'Provider Configuration' ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Saving...
                    </>
                  ) : 'Save'}
                </Button>
              </Card.Header>
              <Collapse in={collapseStates.providerConfig}>
                <Card.Body className="py-2">
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 small fw-bold">Regular Ticker Data Provider</Form.Label>
                  <Form.Select
                    name="ticker_data_provider"
                    value={settings.ticker_data_provider}
                    onChange={handleInputChange}
                    size="sm"
                  >
                    <option value="finnhub">Finnhub.io (Better real-time data)</option>
                    <option value="yahoo_finance">Yahoo Finance (Higher rate limits)</option>
                    <option value="twelvedata">Twelve Data (High quality, global coverage)</option>
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    Provider used for regular stock ticker data
                    {(settings.ticker_data_provider === 'finnhub' || settings.ticker_data_provider === 'twelvedata') && (
                      <Badge bg="info" className="ms-2">Requires API key</Badge>
                    )}
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 small fw-bold">Market Index Data Provider</Form.Label>
                  <Form.Select
                    name="index_data_provider"
                    value={settings.index_data_provider}
                    onChange={handleInputChange}
                    size="sm"
                  >
                    <option value="yahoo_finance">Yahoo Finance (Recommended for indexes)</option>
                    <option value="finnhub" disabled={!providerInfo.finnhub.supportsMarketIndexes}>
                      Finnhub.io (Premium tier only)
                    </option>
                    <option value="twelvedata">Twelve Data (High quality indexes)</option>
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    Provider used for market index data (S&P 500, NASDAQ, etc.)
                    {settings.index_data_provider === 'finnhub' && (
                      <Badge bg="warning" text="dark" className="ms-2">Premium tier required</Badge>
                    )}
                  </Form.Text>
                </Form.Group>
                
                <div className="border-top pt-2 mt-3">
                  <div className="mb-2 small fw-bold">Active Provider Details</div>
                  <div className="small">
                    <div className="mb-1">
                      <Badge bg="primary" className="me-1">Tickers</Badge>
                      {providerInfo[settings.ticker_data_provider] ? providerInfo[settings.ticker_data_provider].name : settings.ticker_data_provider} 
                      {settings.ticker_data_provider === 'finnhub' && !settings.finnhub_api_key && (
                        <Badge bg="danger" className="ms-1">Missing API key</Badge>
                      )}
                      {settings.ticker_data_provider === 'twelvedata' && !settings.twelvedata_api_key && (
                        <Badge bg="danger" className="ms-1">Missing API key</Badge>
                      )}
                    </div>
                    <div className="mb-1">
                      <Badge bg="secondary" className="me-1">Indexes</Badge>
                      {providerInfo[settings.index_data_provider] ? providerInfo[settings.index_data_provider].name : settings.index_data_provider}
                      {settings.index_data_provider === 'finnhub' && (
                        <Badge bg="warning" text="dark" className="ms-1">Premium only</Badge>
                      )}
                      {settings.index_data_provider === 'twelvedata' && !settings.twelvedata_api_key && (
                        <Badge bg="danger" className="ms-1">Missing API key</Badge>
                      )}
                    </div>
                  </div>
                </div>
                </Card.Body>
              </Collapse>
            </Card>
            
            <Card className="mb-3">
              <Card.Header 
                className="py-2 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleCollapse('globalApiSettings')}
              >
                <h5 className="m-0 d-flex align-items-center">
                  <i className={`bi bi-chevron-${collapseStates.globalApiSettings ? 'up' : 'down'} me-2`}></i>
                  Global API Settings
                </h5>
                <Button
                  variant={
                    hasSectionChanges(['global_ticker_update_interval'])
                      ? 'danger'
                      : 'primary'
                  }
                  size="sm"
                  disabled={
                    savingSection === 'Global API Settings' ||
                    !hasSectionChanges(['global_ticker_update_interval'])
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionSave('Global API Settings', [
                      'global_ticker_update_interval'
                    ]);
                  }}
                >
                  {savingSection === 'Global API Settings' ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Saving...
                    </>
                  ) : 'Save'}
                </Button>
              </Card.Header>
              <Collapse in={collapseStates.globalApiSettings}>
                <Card.Body className="py-2">
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small fw-bold">Global Ticker Update Interval (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    name="global_ticker_update_interval"
                    value={settings.global_ticker_update_interval}
                    onChange={handleInputChange}
                    size="sm"
                  />
                  <Form.Text className="text-muted small">Override update interval for all providers (0 = use provider settings)</Form.Text>
                </Form.Group>
                </Card.Body>
              </Collapse>
            </Card>

            <Card className="mb-3">
              <Card.Header 
                className="py-2 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleCollapse('marketHours')}
              >
                <h5 className="m-0 d-flex align-items-center">
                  <i className={`bi bi-chevron-${collapseStates.marketHours ? 'up' : 'down'} me-2`}></i>
                  Market Hours
                </h5>
                <Button
                  variant={
                    hasSectionChanges([
                      'market_hours_start',
                      'market_hours_end',
                      'market_timezone',
                      'extended_hours_enabled',
                      'extended_hours_start',
                      'extended_hours_end'
                    ]) ? 'danger' : 'primary'
                  }
                  size="sm"
                  disabled={
                    savingSection === 'Market Hours' ||
                    !hasSectionChanges([
                      'market_hours_start',
                      'market_hours_end',
                      'market_timezone',
                      'extended_hours_enabled',
                      'extended_hours_start',
                      'extended_hours_end'
                    ])
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionSave('Market Hours', [
                      'market_hours_start',
                      'market_hours_end',
                      'market_timezone',
                      'extended_hours_enabled',
                      'extended_hours_start',
                      'extended_hours_end'
                    ]);
                  }}
                >
                  {savingSection === 'Market Hours' ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Saving...
                    </>
                  ) : 'Save'}
                </Button>
              </Card.Header>
              <Collapse in={collapseStates.marketHours}>
                <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1 small fw-bold">Market Open</Form.Label>
                      <Form.Control
                        type="text"
                        name="market_hours_start"
                        value={settings.market_hours_start}
                        onChange={handleInputChange}
                        size="sm"
                      />
                      <Form.Text className="text-muted small">Format: HH:MM (24h)</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1 small fw-bold">Market Close</Form.Label>
                      <Form.Control
                        type="text"
                        name="market_hours_end"
                        value={settings.market_hours_end}
                        onChange={handleInputChange}
                        size="sm"
                      />
                      <Form.Text className="text-muted small">Format: HH:MM (24h)</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small fw-bold">Timezone</Form.Label>
                  <Form.Control
                    type="text"
                    name="market_timezone"
                    value={settings.market_timezone}
                    onChange={handleInputChange}
                    size="sm"
                  />
                  <Form.Text className="text-muted small">e.g., America/New_York</Form.Text>
                </Form.Group>

                {/* Extended Hours Section */}
                <hr className="my-3" />
                <div className="mb-3">
                  <h6 className="fw-bold text-primary mb-2">🕐 Extended Hours Trading</h6>
                  <Form.Check
                    type="switch"
                    id="extended-hours-switch"
                    label="Enable Extended Hours (Pre-market & After-hours)"
                    checked={settings.extended_hours_enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      extended_hours_enabled: e.target.checked
                    })}
                    className="mb-3"
                  />

                  {settings.extended_hours_enabled && (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-bold">Pre-market Start</Form.Label>
                            <Form.Control
                              type="text"
                              name="extended_hours_start"
                              value={settings.extended_hours_start}
                              onChange={handleInputChange}
                              size="sm"
                            />
                            <Form.Text className="text-muted small">Format: HH:MM (e.g., 4:00)</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-bold">After-hours End</Form.Label>
                            <Form.Control
                              type="text"
                              name="extended_hours_end"
                              value={settings.extended_hours_end}
                              onChange={handleInputChange}
                              size="sm"
                            />
                            <Form.Text className="text-muted small">Format: HH:MM (e.g., 20:00)</Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted">
                          <strong>Extended Hours Schedule:</strong><br />
                          📈 Pre-market: {settings.extended_hours_start} - {settings.market_hours_start}<br />
                          🏛️ Regular Market: {settings.market_hours_start} - {settings.market_hours_end}<br />
                          📉 After-hours: {settings.market_hours_end} - {settings.extended_hours_end}
                        </small>
                      </div>
                    </>
                  )}
                </div>
                </Card.Body>
              </Collapse>
            </Card>
            
            <Card className="mb-3">
              <Card.Header 
                className="py-2 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleCollapse('securitySettings')}
              >
                <h5 className="m-0 d-flex align-items-center">
                  <i className={`bi bi-chevron-${collapseStates.securitySettings ? 'up' : 'down'} me-2`}></i>
                  Security Settings
                </h5>
                <Button
                  variant={
                    hasSectionChanges(['password_expiration_days'])
                      ? 'danger'
                      : 'primary'
                  }
                  size="sm"
                  disabled={
                    savingSection === 'Security Settings' ||
                    !hasSectionChanges(['password_expiration_days'])
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionSave('Security Settings', [
                      'password_expiration_days'
                    ]);
                  }}
                >
                  {savingSection === 'Security Settings' ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Saving...
                    </>
                  ) : 'Save'}
                </Button>
              </Card.Header>
              <Collapse in={collapseStates.securitySettings}>
                <Card.Body className="py-2">
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small fw-bold">Password Expiration (days)</Form.Label>
                  <Form.Control
                    type="number"
                    name="password_expiration_days"
                    value={settings.password_expiration_days}
                    onChange={handleInputChange}
                    size="sm"
                  />
                  <Form.Text className="text-muted small">
                    Number of days after which users must change their password (default: 180 days / 6 months)
                  </Form.Text>
                </Form.Group>
                </Card.Body>
              </Collapse>
            </Card>
          </Col>
          
          {/* Right Column - API Specific Settings */}
          <Col lg={7}>
            <Tabs defaultActiveKey="twelvedata" className="mb-3">
              <Tab eventKey="twelvedata" title={
                <span>
                  Twelve Data
                  {(settings.ticker_data_provider === 'twelvedata' || settings.index_data_provider === 'twelvedata') && (
                    <Badge bg="primary" className="ms-2">Active</Badge>
                  )}
                </span>
              }>
                <Card className="mb-3">
                  <Card.Header 
                    className="py-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollapse('twelvedata')}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="m-0 me-2 d-flex align-items-center">
                        <i className={`bi bi-chevron-${collapseStates.twelvedata ? 'up' : 'down'} me-2`}></i>
                        Twelve Data API Settings
                      </h5>
                      {(settings.ticker_data_provider === 'twelvedata' || settings.index_data_provider === 'twelvedata') && (
                        <Badge bg="primary">In Use</Badge>
                      )}
                    </div>
                    <Button
                      variant={
                        hasSectionChanges([
                          'twelvedata_api_key',
                          'twelvedata_max_requests_per_minute',
                          'twelvedata_rate_limit_capacity',
                          'twelvedata_batch_size'
                        ]) ? 'danger' : 'primary'
                      }
                      size="sm"
                      disabled={
                        savingSection === 'Twelve Data' ||
                        !hasSectionChanges([
                          'twelvedata_api_key',
                          'twelvedata_max_requests_per_minute',
                          'twelvedata_rate_limit_capacity',
                          'twelvedata_batch_size'
                        ])
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSectionSave('Twelve Data', [
                          'twelvedata_api_key',
                          'twelvedata_max_requests_per_minute',
                          'twelvedata_rate_limit_capacity',
                          'twelvedata_batch_size'
                        ]);
                      }}
                    >
                      {savingSection === 'Twelve Data' ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          Saving...
                        </>
                      ) : 'Save'}
                    </Button>
                  </Card.Header>
                  <Collapse in={collapseStates.twelvedata}>
                    <Card.Body className="py-2">
                    <Alert variant="info" className="py-2 mb-3">
                      <div className="small">
                        <strong>Provider Usage:</strong>
                        <ul className="mb-0 ps-3">
                          {settings.ticker_data_provider === 'twelvedata' && (
                            <li>Providing regular ticker data</li>
                          )}
                          {settings.index_data_provider === 'twelvedata' && (
                            <li>Providing market index data</li>
                          )}
                        </ul>
                      </div>
                    </Alert>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">API Key</Form.Label>
                          <Form.Control
                            type="password"
                            name="twelvedata_api_key"
                            value={settings.twelvedata_api_key}
                            onChange={handleInputChange}
                            size="sm"
                            className={settings.ticker_data_provider === 'twelvedata' && !settings.twelvedata_api_key ? 'border-danger' : ''}
                          />
                          <Form.Text className={`small ${settings.ticker_data_provider === 'twelvedata' && !settings.twelvedata_api_key ? 'text-danger' : 'text-muted'}`}>
                            {settings.ticker_data_provider === 'twelvedata' && !settings.twelvedata_api_key 
                              ? 'Required if using Twelve Data for tickers' 
                              : 'Your Twelve Data API key'}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Rate Limit (requests/minute)</Form.Label>
                          <Form.Control
                            type="number"
                            name="twelvedata_max_requests_per_minute"
                            value={settings.twelvedata_max_requests_per_minute}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">
                            Default: 55 requests. Effective safe limit ≈ {twelvedataEffectiveRequestsPerMinute} requests/minute{' '}
                            ({twelvedataEstimatedTickersPerMinute} tickers when fetching quote + price).
                          </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Capacity (%)</Form.Label>
                          <Form.Control
                            type="number"
                            name="twelvedata_rate_limit_capacity"
                            value={twelvedataCapacityDisplayValue === '' ? '' : twelvedataCapacityDisplayValue}
                            onChange={handleInputChange}
                            size="sm"
                            min={5}
                            max={100}
                            step={1}
                          />
                          <Form.Text className="text-muted small">
                            Leave blank to use the default 80% capacity. Requests are throttled to this percentage to stay under
                            Twelve Data's cap.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <div className="mb-2 p-2 bg-light border rounded">
                          <div className="mb-1 small fw-bold text-info">
                            <i className="fas fa-info-circle me-1"></i>
                            Batch Processing Info
                          </div>
                          <div className="text-muted small">
                            <strong>Batch size is fixed at 10 symbols</strong> per batch for optimal API compatibility.
                            Multiple batches are processed automatically within your rate limit above.
                          </div>
                        </div>
                      </Col>
                    </Row>
                    </Card.Body>
                  </Collapse>
                </Card>
              </Tab>

              <Tab eventKey="finnhub" title={
                <span>
                  Finnhub.io
                  {(settings.ticker_data_provider === 'finnhub' || settings.index_data_provider === 'finnhub') && (
                    <Badge bg="primary" className="ms-2">Active</Badge>
                  )}
                </span>
              }>
                <Card className="mb-3">
                  <Card.Header 
                    className="py-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollapse('finnhub')}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="m-0 me-2 d-flex align-items-center">
                        <i className={`bi bi-chevron-${collapseStates.finnhub ? 'up' : 'down'} me-2`}></i>
                        Finnhub.io API Settings
                      </h5>
                      {(settings.ticker_data_provider === 'finnhub' || settings.index_data_provider === 'finnhub') && (
                        <Badge bg="primary">In Use</Badge>
                      )}
                    </div>
                    <Button
                      variant={
                        hasSectionChanges([
                          'finnhub_api_key',
                          'finnhub_max_requests_per_hour',
                          'finnhub_min_update_interval'
                        ]) ? 'danger' : 'primary'
                      }
                      size="sm"
                      disabled={
                        savingSection === 'Finnhub' ||
                        !hasSectionChanges([
                          'finnhub_api_key',
                          'finnhub_max_requests_per_hour',
                          'finnhub_min_update_interval'
                        ])
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSectionSave('Finnhub', [
                          'finnhub_api_key',
                          'finnhub_max_requests_per_hour',
                          'finnhub_min_update_interval'
                        ]);
                      }}
                    >
                      {savingSection === 'Finnhub' ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          Saving...
                        </>
                      ) : 'Save'}
                    </Button>
                  </Card.Header>
                  <Collapse in={collapseStates.finnhub}>
                    <Card.Body className="py-2">
                    <Alert variant="info" className="py-2 mb-3">
                      <div className="small">
                        <strong>Provider Usage:</strong>
                        <ul className="mb-0 ps-3">
                          {settings.ticker_data_provider === 'finnhub' && (
                            <li>Providing regular ticker data</li>
                          )}
                          {settings.index_data_provider === 'finnhub' && (
                            <li>Providing market index data (requires premium)</li>
                          )}
                        </ul>
                      </div>
                    </Alert>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">API Key</Form.Label>
                          <Form.Control
                            type="password"
                            name="finnhub_api_key"
                            value={settings.finnhub_api_key}
                            onChange={handleInputChange}
                            size="sm"
                            className={settings.ticker_data_provider === 'finnhub' && !settings.finnhub_api_key ? 'border-danger' : ''}
                          />
                          <Form.Text className={`small ${settings.ticker_data_provider === 'finnhub' && !settings.finnhub_api_key ? 'text-danger' : 'text-muted'}`}>
                            {settings.ticker_data_provider === 'finnhub' && !settings.finnhub_api_key 
                              ? 'Required if using Finnhub for tickers' 
                              : 'Your Finnhub API key'}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Rate Limit (requests/hour)</Form.Label>
                          <Form.Control
                            type="number"
                            name="finnhub_max_requests_per_hour"
                            value={settings.finnhub_max_requests_per_hour}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">Default: 60 (Free tier)</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Min Update Interval (seconds)</Form.Label>
                          <Form.Control
                            type="number"
                            name="finnhub_min_update_interval"
                            value={settings.finnhub_min_update_interval}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">Recommended: 60+</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    </Card.Body>
                  </Collapse>
                </Card>
              </Tab>

              <Tab eventKey="yahoo-finance" title={
                <span>
                  Yahoo Finance
                  {(settings.ticker_data_provider === 'yahoo_finance' || settings.index_data_provider === 'yahoo_finance') && (
                    <Badge bg="primary" className="ms-2">Active</Badge>
                  )}
                </span>
              }>
                <Card className="mb-3">
                  <Card.Header 
                    className="py-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollapse('yahooFinance')}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="m-0 me-2 d-flex align-items-center">
                        <i className={`bi bi-chevron-${collapseStates.yahooFinance ? 'up' : 'down'} me-2`}></i>
                        Yahoo Finance API Settings
                      </h5>
                      {(settings.ticker_data_provider === 'yahoo_finance' || settings.index_data_provider === 'yahoo_finance') && (
                        <Badge bg="primary">In Use</Badge>
                      )}
                    </div>
                    <Button
                      variant={
                        hasSectionChanges([
                          'yahoo_finance_api_key',
                          'yahoo_finance_max_requests_per_hour',
                          'yahoo_finance_min_update_interval',
                          'yahoo_finance_endpoint',
                          'yahoo_finance_rate_limit'
                        ]) ? 'danger' : 'primary'
                      }
                      size="sm"
                      disabled={
                        savingSection === 'Yahoo Finance' ||
                        !hasSectionChanges([
                          'yahoo_finance_api_key',
                          'yahoo_finance_max_requests_per_hour',
                          'yahoo_finance_min_update_interval',
                          'yahoo_finance_endpoint',
                          'yahoo_finance_rate_limit'
                        ])
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSectionSave('Yahoo Finance', [
                          'yahoo_finance_api_key',
                          'yahoo_finance_max_requests_per_hour',
                          'yahoo_finance_min_update_interval',
                          'yahoo_finance_endpoint',
                          'yahoo_finance_rate_limit'
                        ]);
                      }}
                    >
                      {savingSection === 'Yahoo Finance' ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          Saving...
                        </>
                      ) : 'Save'}
                    </Button>
                  </Card.Header>
                  <Collapse in={collapseStates.yahooFinance}>
                    <Card.Body className="py-2">
                    <Alert variant="info" className="py-2 mb-3">
                      <div className="small">
                        <strong>Provider Usage:</strong>
                        <ul className="mb-0 ps-3">
                          {settings.ticker_data_provider === 'yahoo_finance' && (
                            <li>Providing regular ticker data</li>
                          )}
                          {settings.index_data_provider === 'yahoo_finance' && (
                            <li>Providing market index data</li>
                          )}
                        </ul>
                      </div>
                    </Alert>
                  
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">API Key</Form.Label>
                          <Form.Control
                            type="password"
                            name="yahoo_finance_api_key"
                            value={settings.yahoo_finance_api_key}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">
                            Optional - only needed for official API
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Rate Limit (requests/hour)</Form.Label>
                          <Form.Control
                            type="number"
                            name="yahoo_finance_max_requests_per_hour"
                            value={settings.yahoo_finance_max_requests_per_hour}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">Default: 500</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">Min Update Interval (seconds)</Form.Label>
                          <Form.Control
                            type="number"
                            name="yahoo_finance_min_update_interval"
                            value={settings.yahoo_finance_min_update_interval}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">Recommended: 60+</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-bold">API Endpoint</Form.Label>
                          <Form.Control
                            type="text"
                            name="yahoo_finance_endpoint"
                            value={settings.yahoo_finance_endpoint}
                            onChange={handleInputChange}
                            size="sm"
                          />
                          <Form.Text className="text-muted small">For official API only</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    </Card.Body>
                  </Collapse>
                </Card>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Form>
  );
}

// System Monitor Tab Component
function SystemMonitorTab() {
  const [systemData, setSystemData] = useState({
    resources: null,
    health: null,
    performance: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMonitoringData = async () => {
    try {
      setError('');
      
      const [resourcesRes, healthRes, performanceRes] = await Promise.all([
        getSystemResources(),
        getApplicationHealth(),
        getPerformanceMetrics()
      ]);

      setSystemData({
        resources: resourcesRes.data,
        health: healthRes.data,
        performance: performanceRes.data
      });
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError('Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoringData();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchMonitoringData, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const formatUptime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${hours.toFixed(1)} hours`;
    return `${Math.floor(hours / 24)} days, ${Math.round(hours % 24)} hours`;
  };

  const getStatusBadge = (isHealthy) => (
    <Badge bg={isHealthy ? 'success' : 'danger'}>
      {isHealthy ? 'Healthy' : 'Error'}
    </Badge>
  );

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading system metrics...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
      
      {/* Controls */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Form.Check 
                type="switch"
                id="auto-refresh-switch"
                label="Auto-refresh (5s)"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            </div>
            <Button variant="outline-secondary" size="sm" onClick={fetchMonitoringData}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh Now
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        {/* System Resources */}
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header className="py-2">
              <h5 className="m-0">🖥️ System Resources</h5>
              <small className="text-muted">
                Last updated: {systemData.resources?.timestamp ? 
                  new Date(systemData.resources.timestamp).toLocaleTimeString() : 'N/A'}
              </small>
            </Card.Header>
            <Card.Body className="py-2">
              {systemData.resources ? (
                <>
                  {/* CPU */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <small className="fw-bold">CPU Usage</small>
                      <small>{systemData.resources.cpu.percent.toFixed(1)}%</small>
                    </div>
                    <ProgressBar 
                      now={systemData.resources.cpu.percent} 
                      variant={systemData.resources.cpu.percent > 80 ? 'danger' : systemData.resources.cpu.percent > 60 ? 'warning' : 'success'}
                      className="mb-1"
                    />
                    <small className="text-muted">Cores: {systemData.resources.cpu.count}</small>
                  </div>

                  {/* Memory */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <small className="fw-bold">Memory Usage</small>
                      <small>{systemData.resources.memory.percent.toFixed(1)}%</small>
                    </div>
                    <ProgressBar 
                      now={systemData.resources.memory.percent} 
                      variant={systemData.resources.memory.percent > 80 ? 'danger' : systemData.resources.memory.percent > 60 ? 'warning' : 'success'}
                      className="mb-1"
                    />
                    <small className="text-muted">
                      {systemData.resources.memory.used_gb}GB / {systemData.resources.memory.total_gb}GB
                    </small>
                  </div>

                  {/* Disk */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <small className="fw-bold">Disk Usage</small>
                      <small>{systemData.resources.disk.percent}%</small>
                    </div>
                    <ProgressBar 
                      now={systemData.resources.disk.percent} 
                      variant={systemData.resources.disk.percent > 80 ? 'danger' : systemData.resources.disk.percent > 60 ? 'warning' : 'success'}
                      className="mb-1"
                    />
                    <small className="text-muted">
                      {systemData.resources.disk.used_gb}GB / {systemData.resources.disk.total_gb}GB
                    </small>
                  </div>

                  {/* Process Info */}
                  <div className="border-top pt-2">
                    <small className="fw-bold d-block mb-1">Flask Process</small>
                    <small className="text-muted">
                      PID: {systemData.resources.process.pid} | 
                      Memory: {systemData.resources.process.memory_mb}MB | 
                      CPU: {systemData.resources.process.cpu_percent}%
                    </small>
                  </div>
                </>
              ) : (
                <p className="text-muted">No resource data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Application Health */}
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header className="py-2">
              <h5 className="m-0">🏥 Application Health</h5>
              <small className="text-muted">
                Last updated: {systemData.health?.timestamp ? 
                  new Date(systemData.health.timestamp).toLocaleTimeString() : 'N/A'}
              </small>
            </Card.Header>
            <Card.Body className="py-2">
              {systemData.health ? (
                <>
                  {/* Database */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Database</span>
                      {getStatusBadge(systemData.health.database.healthy)}
                    </div>
                    <small className="text-muted d-block">
                      Active connections: {systemData.health.database.active_connections}
                    </small>
                  </div>

                  {/* SocketIO */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">SocketIO Clients</span>
                      <Badge bg="info">{systemData.health.socketio.active_clients}</Badge>
                    </div>
                  </div>

                  {/* Application Uptime */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <span className="fw-bold">Uptime</span>
                      <Badge bg="secondary">
                        {formatUptime(systemData.health.application.uptime_hours)}
                      </Badge>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted">No health data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Performance Metrics */}
        <Col lg={12}>
          <Card className="mb-3">
            <Card.Header className="py-2">
              <h5 className="m-0">📊 Performance & Services</h5>
              <small className="text-muted">
                Last updated: {systemData.performance?.timestamp ? 
                  new Date(systemData.performance.timestamp).toLocaleTimeString() : 'N/A'}
              </small>
            </Card.Header>
            <Card.Body className="py-2">
              {systemData.performance ? (
                <Row>
                  {/* Ticker Service */}
                  <Col md={6}>
                    <h6 className="fw-bold mb-2">🎯 Ticker Service</h6>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>Lock Status</span>
                        {getStatusBadge(systemData.performance.ticker_service.lock_held)}
                      </div>
                      <small className="text-muted">
                        Server ID: {systemData.performance.ticker_service.server_id}
                      </small>
                    </div>
                    {systemData.performance.ticker_service.rate_limits && (
                      <div>
                        <small className="fw-bold d-block">Rate Limits:</small>
                        {Object.entries(systemData.performance.ticker_service.rate_limits).map(([provider, limits]) => (
                          <small key={provider} className="d-block text-muted">
                            {provider}: {limits.current || 0}/{limits.limit || 'N/A'}
                          </small>
                        ))}
                      </div>
                    )}
                  </Col>

                  {/* Ticker Stats */}
                  <Col md={6}>
                    <h6 className="fw-bold mb-2">📈 Ticker Statistics</h6>
                    <div className="mb-2">
                      <span className="fw-bold">Total Tickers: </span>
                      <Badge bg="primary">{systemData.performance.ticker_stats.total_tickers}</Badge>
                    </div>
                    <div>
                      <small className="fw-bold d-block mb-1">Recent Updates:</small>
                      {systemData.performance.ticker_stats.recent_updates.slice(0, 3).map((ticker, index) => (
                        <small key={index} className="d-block text-muted">
                          {ticker.symbol}: ${ticker.current_price?.toFixed(2) || 'N/A'} 
                          {ticker.last_updated && (
                            <span> ({new Date(ticker.last_updated).toLocaleTimeString()})</span>
                          )}
                        </small>
                      ))}
                    </div>
                  </Col>
                </Row>
              ) : (
                <p className="text-muted">No performance data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// Backend Debug Tab Component
function DebugConsoleTab({ settings, handleInputChange, handleSectionSave, savingSection, collapseStates, toggleCollapse, hasSectionChanges }) {
  const [debugSwitches, setDebugSwitches] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [savingModule, setSavingModule] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});

  const fetchDebugSwitches = async () => {
    try {
      setError('');
      const response = await getDebugSwitches();
      setDebugSwitches(response.data.switches);
    } catch (err) {
      console.error('Error fetching debug switches:', err);
      setError('Failed to fetch debug switches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebugSwitches();
  }, []);

  const handleSwitchChange = async (moduleName, field, value) => {
    try {
      setSavingModule(moduleName);
      
      const updatedSwitches = {
        ...debugSwitches,
        [moduleName]: {
          ...debugSwitches[moduleName],
          [field]: value
        }
      };

      await updateDebugSwitches(updatedSwitches);
      setDebugSwitches(updatedSwitches);
      setSuccessMessage(`Updated ${moduleName} debug settings`);
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating debug switches:', err);
      setError(`Failed to update ${moduleName} settings`);
    } finally {
      setSavingModule('');
    }
  };

  const getModuleDisplayName = (moduleName) => {
    const names = {
      ticker_service: 'Ticker Service',
      database: 'Database',
      websocket: 'WebSocket',
      api_requests: 'API Requests',
      yahoo_finance: 'Yahoo Finance',
      finnhub: 'Finnhub',
      twelvedata: 'Twelve Data',
      lock_manager: 'Lock Manager',
      tickers: 'Tickers',
      accounts: 'Accounts',
      socket_service: 'Socket Service',
      api_search: 'API Search',
      api_portfolios: 'API Portfolios',
      api_orders: 'API Orders',
      api_trades: 'API Trades',
      api_settings: 'API Settings',
      api_transactions: 'API Transactions',
      api_ticker_shields: 'API Ticker Shields',
      api_scheduler_health: 'API Scheduler Health',
      api_system_activities: 'API System Activities',
      service_trailing_stop: 'Trailing Stop Service',
      service_intraday_price: 'Intraday Price Service',
      service_daily_statistics: 'Daily Statistics Service',
      service_distributed_ticker: 'Distributed Ticker Service',
      service_high_price_tracking: 'High Price Tracking Service',
      service_shield_risk: 'Shield Risk Service',
      service_rate_limit: 'Rate Limit Service',
      service_shield_adjustment: 'Shield Adjustment Service',
      service_finance_manager: 'Finance Manager Service',
      core_app: 'Core App'
    };
    return names[moduleName] || moduleName;
  };

  const getModuleIcon = (moduleName) => {
    const icons = {
      ticker_service: '🎯',
      database: '🗄️',
      websocket: '🔌',
      api_requests: '🌐',
      yahoo_finance: '📈',
      finnhub: '📊',
      twelvedata: '📉',
      lock_manager: '🔒',
      tickers: '🏷️',
      accounts: '👤',
      socket_service: '🔌',
      api_search: '🔍',
      api_portfolios: '📁',
      api_orders: '📋',
      api_trades: '💼',
      api_settings: '⚙️',
      api_transactions: '💳',
      api_ticker_shields: '🛡️',
      api_scheduler_health: '🕐',
      api_system_activities: '📊',
      service_trailing_stop: '📉',
      service_intraday_price: '📈',
      service_daily_statistics: '📊',
      service_distributed_ticker: '🌐',
      service_high_price_tracking: '📊',
      service_shield_risk: '🛡️',
      service_rate_limit: '⏰',
      service_shield_adjustment: '⚖️',
      service_finance_manager: '💰',
      core_app: '⚡'
    };
    return icons[moduleName] || '🔧';
  };

  const formatExpiration = (expiresAt) => {
    if (!expiresAt) return null;
    try {
      const expireTime = new Date(expiresAt);
      const now = new Date();
      const diffMinutes = Math.ceil((expireTime - now) / (1000 * 60));
      
      if (diffMinutes <= 0) return 'Expired';
      if (diffMinutes < 60) return `${diffMinutes}m remaining`;
      return `${Math.ceil(diffMinutes / 60)}h remaining`;
    } catch {
      return null;
    }
  };

  // Filter debug switches based on status and search term
  const filteredSwitches = Object.entries(debugSwitches).filter(([moduleName, config]) => {
    // First filter by status
    if (statusFilter === 'active' && !config.enabled) return false;
    if (statusFilter === 'inactive' && config.enabled) return false;

    // Then filter by search term
    if (!searchFilter) return true;
    const displayName = getModuleDisplayName(moduleName).toLowerCase();
    const moduleKey = moduleName.toLowerCase();
    const searchTerm = searchFilter.toLowerCase();
    return displayName.includes(searchTerm) || moduleKey.includes(searchTerm);
  });

  // Calculate counts for filter buttons
  const allSwitches = Object.entries(debugSwitches);
  const activeSwitchesCount = allSwitches.filter(([, config]) => config.enabled).length;
  const inactiveSwitchesCount = allSwitches.filter(([, config]) => !config.enabled).length;

  // Toggle card expansion
  const toggleCardExpansion = (moduleName) => {
    setExpandedCards(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  // Expand all cards
  const expandAllCards = () => {
    const allExpanded = {};
    filteredSwitches.forEach(([moduleName]) => {
      allExpanded[moduleName] = true;
    });
    setExpandedCards(allExpanded);
  };

  // Collapse all cards  
  const collapseAllCards = () => {
    setExpandedCards({});
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading debug console...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
      {successMessage && <Alert variant="success" className="py-2 mb-3">{successMessage}</Alert>}
      
      {/* Global Debug Settings */}
      <Row className="mb-3">
        <Col lg={12}>
          <Card>
            <Card.Header
              className="py-2 d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleCollapse('globalDebugSettings')}
            >
              <h6 className="m-0 d-flex align-items-center">
                <i className={`bi bi-chevron-${collapseStates.globalDebugSettings ? 'up' : 'down'} me-2`}></i>
                ⚙️ Global Debug Settings
              </h6>
              <Button
                variant={
                  hasSectionChanges(['default_debug_log_level'])
                    ? 'danger'
                    : 'primary'
                }
                size="sm"
                disabled={
                  savingSection === 'Backend Debug Settings' ||
                  !hasSectionChanges(['default_debug_log_level'])
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleSectionSave('Backend Debug Settings', ['default_debug_log_level']);
                }}
              >
                {savingSection === 'Backend Debug Settings' ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Saving...
                  </>
                ) : 'Save'}
              </Button>
            </Card.Header>
            <Collapse in={collapseStates.globalDebugSettings}>
              <Card.Body className="py-2">
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-bold">Default Log Level</Form.Label>
                      <Form.Select
                        size="sm"
                        name="default_debug_log_level"
                        value={settings.default_debug_log_level}
                        onChange={handleInputChange}
                      >
                        <option value="DEBUG">DEBUG</option>
                        <option value="INFO">INFO</option>
                        <option value="WARNING">WARNING</option>
                        <option value="ERROR">ERROR</option>
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Default log level for new debug switches
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <div className="small text-muted pt-3">
                      This setting controls the default log level used when creating new debug switches.
                      Existing debug switches will retain their current log level until manually changed.
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      {/* Controls Header */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-0">🐛 Module Debug Controls</h6>
              <small className="text-muted">Enable debug logging for specific modules with auto-timeout</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button variant="outline-info" size="sm" onClick={expandAllCards}>
                <i className="bi bi-arrows-expand me-1"></i>
                Expand All
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={collapseAllCards}>
                <i className="bi bi-arrows-collapse me-1"></i>
                Collapse All
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={fetchDebugSwitches}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Status Filter */}
          <Row className="mb-3">
            <Col md={6}>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted me-2">Filter:</small>
                <ButtonGroup size="sm">
                  <Button
                    variant={statusFilter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchFilter('');
                    }}
                  >
                    All ({allSwitches.length})
                  </Button>
                  <Button
                    variant={statusFilter === 'active' ? 'success' : 'outline-success'}
                    onClick={() => {
                      setStatusFilter('active');
                      setSearchFilter('');
                    }}
                  >
                    Active ({activeSwitchesCount})
                  </Button>
                  <Button
                    variant={statusFilter === 'inactive' ? 'secondary' : 'outline-secondary'}
                    onClick={() => {
                      setStatusFilter('inactive');
                      setSearchFilter('');
                    }}
                  >
                    Inactive ({inactiveSwitchesCount})
                  </Button>
                </ButtonGroup>
              </div>
            </Col>
            <Col md={6}>
              <small className="text-muted">
                {statusFilter !== 'all' && `Filtered by: ${statusFilter} • `}
                Showing {filteredSwitches.length} modules
              </small>
            </Col>
          </Row>

          {/* Search Filter */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="🔍 Search modules... (e.g., 'api', 'service', 'shield')"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
                {searchFilter && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchFilter('')}
                    title="Clear search"
                    style={{ borderLeft: 'none' }}
                  >
                    <i className="bi bi-x"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={6}>
              <small className="text-muted">
                {searchFilter && `Search: "${searchFilter}" • `}
                {statusFilter !== 'all' ? `${statusFilter} modules` : 'All modules'}
              </small>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Debug Switches */}
      {filteredSwitches.length === 0 ? (
        <Row>
          <Col>
            <div className="text-center py-4">
              <div className="text-muted">
                <i className="bi bi-search fs-1 mb-3 d-block"></i>
                <h5>No modules found</h5>
                <p>
                  {searchFilter ? 
                    <>No modules match "<strong>{searchFilter}</strong>". Try a different search term.</> : 
                    'No debug modules available.'
                  }
                </p>
                {searchFilter && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearchFilter('')}>
                    Clear Search
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      ) : (
        <Row>
          {filteredSwitches.map(([moduleName, config]) => (
          <Col lg={6} key={moduleName} className="mb-3">
            <Card className="h-100">
              <Card.Header 
                className="py-2 cursor-pointer" 
                onClick={() => toggleCardExpansion(moduleName)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 me-2 text-decoration-none"
                      onClick={(e) => { e.stopPropagation(); toggleCardExpansion(moduleName); }}
                    >
                      <i className={`bi ${expandedCards[moduleName] ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
                    </Button>
                    <h6 className="m-0">
                      {getModuleIcon(moduleName)} {getModuleDisplayName(moduleName)}
                    </h6>
                  </div>
                  <div className="d-flex align-items-center">
                    {config.enabled && (
                      <Badge bg="success" className="me-2">
                        {config.expires_at ? formatExpiration(config.expires_at) : 'Active'}
                      </Badge>
                    )}
                    {savingModule === moduleName && (
                      <Spinner animation="border" size="sm" className="me-2" />
                    )}
                    <Form.Check
                      type="switch"
                      id={`switch-${moduleName}`}
                      checked={config.enabled}
                      onChange={(e) => { e.stopPropagation(); handleSwitchChange(moduleName, 'enabled', e.target.checked); }}
                      disabled={savingModule === moduleName}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </Card.Header>
              <Collapse in={expandedCards[moduleName]}>
                <div>
                  <Card.Body className="py-2">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small fw-bold">Log Level</Form.Label>
                          <Form.Select
                            size="sm"
                            value={config.log_level}
                            onChange={(e) => handleSwitchChange(moduleName, 'log_level', e.target.value)}
                            disabled={!config.enabled || savingModule === moduleName}
                          >
                            <option value="DEBUG">DEBUG</option>
                            <option value="INFO">INFO</option>
                            <option value="WARNING">WARNING</option>
                            <option value="ERROR">ERROR</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small fw-bold">Auto-timeout (minutes)</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max="1440"
                            value={config.timeout_minutes}
                            onChange={(e) => handleSwitchChange(moduleName, 'timeout_minutes', parseInt(e.target.value) || 0)}
                            disabled={!config.enabled || savingModule === moduleName}
                          />
                          <Form.Text className="text-muted small">0 = no timeout</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {config.enabled && (
                      <div className="border-top pt-2 mt-2">
                        <small className="text-muted">
                          <div>Status: <Badge bg={config.enabled ? 'success' : 'secondary'} className="small">
                            {config.enabled ? 'Enabled' : 'Disabled'}
                          </Badge></div>
                          <div>Level: <code>{config.log_level}</code></div>
                          {config.expires_at && (
                            <div>Expires: <code>{formatExpiration(config.expires_at)}</code></div>
                          )}
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </div>
              </Collapse>
            </Card>
          </Col>
          ))}
        </Row>
      )}

    </div>
  );
}

// Thread Manager Tab Component
function ThreadManagerTab() {
  const [threads, setThreads] = useState([]);
  const [threadHealth, setThreadHealth] = useState(null);
  const [duplicates, setDuplicates] = useState({ detected: 0, groups: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState(new Set(['all']));
  const [controlLoading, setControlLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({ show: false, thread: null, action: null, warnings: [] });

  const fetchThreadData = async () => {
    try {
      setError('');
      
      const [threadsRes, healthRes] = await Promise.all([
        getThreads(),
        getThreadHealth()
      ]);

      setThreads(threadsRes.data.threads);
      setThreadHealth(healthRes.data.health);
      setDuplicates(threadsRes.data.duplicates || { detected: 0, groups: {} });
    } catch (err) {
      console.error('Error fetching thread data:', err);
      setError('Failed to fetch thread data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreadData();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchThreadData, 3000); // Refresh every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const handleLearnThreads = async () => {
    try {
      setError('');
      const response = await learnThreads();
      setSuccessMessage(`Learned ${response.data.learned_count} new thread patterns`);
      
      // Refresh thread data after learning
      await fetchThreadData();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error learning threads:', err);
      setError('Failed to learn new threads');
    }
  };

  const handleThreadControl = async (thread, action) => {
    try {
      setError('');
      setControlLoading(true);
      
      // First try without confirmation to check for safety warnings
      const response = await controlThread(thread.thread_id, thread.name, action, false);
      
      if (response.data.requires_confirmation) {
        // Show confirmation modal with warnings
        setConfirmationModal({
          show: true,
          thread: thread,
          action: action,
          warnings: response.data.warnings || [response.data.warning].filter(Boolean)
        });
        return;
      }
      
      // Action completed successfully
      setSuccessMessage(`Thread ${action} completed: ${response.data.result.message}`);
      await fetchThreadData();
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err) {
      console.error('Error controlling thread:', err);
      if (err.response?.data?.requires_confirmation) {
        setConfirmationModal({
          show: true,
          thread: thread,
          action: action,
          warnings: err.response.data.warnings || [err.response.data.warning].filter(Boolean)
        });
      } else {
        setError(err.response?.data?.error || 'Failed to control thread');
      }
    } finally {
      setControlLoading(false);
    }
  };

  const handleConfirmedThreadControl = async () => {
    try {
      setError('');
      setControlLoading(true);
      
      const { thread, action } = confirmationModal;
      
      // Execute with confirmation
      const response = await controlThread(thread.thread_id, thread.name, action, true);
      
      setConfirmationModal({ show: false, thread: null, action: null, warnings: [] });
      setSuccessMessage(`Thread ${action} completed: ${response.data.result.message}`);
      
      await fetchThreadData();
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err) {
      console.error('Error in confirmed thread control:', err);
      setError(err.response?.data?.error || 'Failed to control thread');
      setConfirmationModal({ show: false, thread: null, action: null, warnings: [] });
    } finally {
      setControlLoading(false);
    }
  };

  const isThreadControllable = (thread) => {
    return thread.config?.controllable === true;
  };

  const getStatusBadge = (thread) => {
    switch (thread.status) {
      case 'active':
        if (thread.is_duplicate) {
          return <Badge bg="success">Active {thread.duplicate_index}/{thread.duplicate_count}</Badge>;
        }
        if (thread.daemon) return <Badge bg="success">Active (Daemon)</Badge>;
        return <Badge bg="success">Active</Badge>;
      case 'duplicate':
        return <Badge bg="warning">Duplicate {thread.duplicate_index}/{thread.duplicate_count}</Badge>;
      case 'missing':
        return <Badge bg="danger">Missing</Badge>;
      case 'unexpected':
        return <Badge bg="warning">Unexpected</Badge>;
      default:
        if (!thread.alive) return <Badge bg="danger">Dead</Badge>;
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'critical_always': '🔴',
      'application_services': '⚙️',
      'system_threads': '🔧',
      'web_server': '🌐',
      'ticker_service': '📈',
      'websocket': '🔌',
      'scheduler': '⏰',
      'system': '⚡',
      'learned': '🔍',
      'unknown': '❓',
      'unexpected': '⚠️'
    };
    return icons[category] || '📋';
  };

  const getHealthStatusBadge = (status) => {
    const variants = {
      'healthy': 'success',
      'warning': 'warning', 
      'critical': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const filteredThreads = threads.filter(thread => {
    // If 'all' is selected, show everything
    if (selectedCategories.has('all')) {
      return true;
    }
    
    // Check status-based filters
    if (selectedCategories.has('baseline') && thread.is_baseline) return true;
    if (selectedCategories.has('active') && (thread.status === 'active' || thread.status === 'duplicate')) return true;
    if (selectedCategories.has('missing') && thread.status === 'missing') return true;
    if (selectedCategories.has('unexpected') && thread.status === 'unexpected') return true;
    if (selectedCategories.has('duplicates') && thread.is_duplicate) return true;
    
    // Check category-based filters
    if (selectedCategories.has(thread.category)) return true;
    
    return false;
  });

  const getFilterOptions = () => {
    const categories = new Set(threads.map(t => t.category));
    const statusOptions = [
      { value: 'all', label: 'All Threads', count: threads.length },
      { value: 'baseline', label: 'Baseline', count: threads.filter(t => t.is_baseline).length },
      { value: 'active', label: 'Active', count: threads.filter(t => t.status === 'active' || t.status === 'duplicate').length },
      { value: 'missing', label: 'Missing', count: threads.filter(t => t.status === 'missing').length },
      { value: 'unexpected', label: 'Unexpected', count: threads.filter(t => t.status === 'unexpected').length },
      ...(duplicates.detected > 0 ? [{ value: 'duplicates', label: 'Duplicates', count: threads.filter(t => t.is_duplicate).length }] : [])
    ];
    const categoryOptions = Array.from(categories).sort().map(cat => ({
      value: cat,
      label: cat.replace('_', ' '),
      count: threads.filter(t => t.category === cat).length,
      icon: getCategoryIcon(cat)
    }));
    
    return { statusOptions, categoryOptions };
  };

  const handleCategoryToggle = (category) => {
    const newSelected = new Set(selectedCategories);
    
    if (category === 'all') {
      // If 'all' is clicked, either select only 'all' or deselect everything
      if (newSelected.has('all')) {
        newSelected.clear();
      } else {
        newSelected.clear();
        newSelected.add('all');
      }
    } else {
      // Remove 'all' if selecting specific categories
      newSelected.delete('all');
      
      // Toggle the category
      if (newSelected.has(category)) {
        newSelected.delete(category);
      } else {
        newSelected.add(category);
      }
      
      // If no categories selected, default back to 'all'
      if (newSelected.size === 0) {
        newSelected.add('all');
      }
    }
    
    setSelectedCategories(newSelected);
  };

  const selectAllCategories = () => {
    const { statusOptions, categoryOptions } = getFilterOptions();
    const newSelected = new Set();
    statusOptions.forEach(opt => newSelected.add(opt.value));
    categoryOptions.forEach(opt => newSelected.add(opt.value));
    newSelected.delete('all'); // Remove 'all' since we're selecting specific ones
    setSelectedCategories(newSelected);
  };

  const clearAllCategories = () => {
    setSelectedCategories(new Set(['all']));
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading thread data...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
      {successMessage && <Alert variant="success" className="py-2 mb-3">{successMessage}</Alert>}
      
      {/* Thread Health Overview */}
      <Row className="mb-3">
        <Col lg={12}>
          <Card>
            <Card.Header className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="m-0">🏥 Thread Health Overview</h6>
                {threadHealth && getHealthStatusBadge(threadHealth.status)}
              </div>
            </Card.Header>
            <Card.Body className="py-2">
              <Row>
                <Col md={6} lg={3}>
                  <div className="text-center">
                    <h5 className="mb-0">{threads.filter(t => t.is_baseline).length}</h5>
                    <small className="text-muted">Baseline</small>
                  </div>
                </Col>
                <Col md={6} lg={3}>
                  <div className="text-center">
                    <h5 className="mb-0 text-success">{threads.filter(t => t.status === 'active' || t.status === 'duplicate').length}</h5>
                    <small className="text-muted">Active</small>
                  </div>
                </Col>
                <Col md={6} lg={3}>
                  <div className="text-center">
                    <h5 className="mb-0 text-danger">{threads.filter(t => t.status === 'missing').length}</h5>
                    <small className="text-muted">Missing</small>
                  </div>
                </Col>
                <Col md={6} lg={3}>
                  <div className="text-center">
                    <h5 className="mb-0 text-warning">{threads.filter(t => t.status === 'unexpected' || t.is_duplicate).length}</h5>
                    <small className="text-muted">Issues</small>
                  </div>
                </Col>
              </Row>
              
              {duplicates.detected > 0 && (
                <Row className="mt-2">
                  <Col>
                    <Alert variant="warning" className="py-2 mb-2 small">
                      <strong>⚠️ {duplicates.detected} Duplicate Group{duplicates.detected > 1 ? 's' : ''} Detected:</strong>
                      <ul className="mb-0 mt-1">
                        {Object.entries(duplicates.groups).map(([key, group]) => (
                          <li key={key}>
                            <code>{key}</code>: {group.actual} running (expected {group.expected})
                            <Badge bg={group.severity === 'critical' ? 'danger' : 'warning'} className="ms-1 small">
                              {group.severity}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  </Col>
                </Row>
              )}
              
              {threads.filter(t => t.status === 'missing' || t.health_status === 'critical').length > 0 && (
                <div className="mt-3 pt-2 border-top">
                  {threads.filter(t => t.status === 'missing' && t.config?.required).map((missing, index) => (
                    <Alert key={index} variant="danger" className="py-1 mb-1 small">
                      <strong>Critical Missing:</strong> {missing.config.description} ({missing.baseline_key})
                    </Alert>
                  ))}
                  {threads.filter(t => t.status === 'missing' && !t.config?.required).map((missing, index) => (
                    <Alert key={index} variant="warning" className="py-1 mb-1 small">
                      <strong>Missing Optional:</strong> {missing.config.description} ({missing.baseline_key})
                    </Alert>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Controls */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <Form.Check 
                type="switch"
                id="auto-refresh-threads"
                label="Auto-refresh (3s)"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            </div>
            
            <div className="d-flex gap-2">
              <Button variant="outline-info" size="sm" onClick={handleLearnThreads}>
                <i className="bi bi-lightbulb me-1"></i>
                Learn Unknown
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={fetchThreadData}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Category Filter Controls */}
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="m-0">🏷️ Filter Categories ({selectedCategories.has('all') ? 'All' : selectedCategories.size})</h6>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={selectAllCategories}>
                    Select All
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={clearAllCategories}>
                    Clear
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="py-2">
              {(() => {
                const { statusOptions, categoryOptions } = getFilterOptions();
                return (
                  <Row>
                    {/* Status Filters */}
                    <Col md={6}>
                      <div className="small fw-bold mb-2 text-muted">Status Filters:</div>
                      <div className="d-flex flex-wrap gap-2">
                        {statusOptions.map(option => (
                          <Form.Check
                            key={option.value}
                            type="checkbox"
                            id={`filter-${option.value}`}
                            label={`${option.label} (${option.count})`}
                            checked={selectedCategories.has(option.value)}
                            onChange={() => handleCategoryToggle(option.value)}
                            className="me-3 small"
                          />
                        ))}
                      </div>
                    </Col>
                    
                    {/* Category Filters */}
                    <Col md={6}>
                      <div className="small fw-bold mb-2 text-muted">Thread Categories:</div>
                      <div className="d-flex flex-wrap gap-2">
                        {categoryOptions.map(option => (
                          <Form.Check
                            key={option.value}
                            type="checkbox"
                            id={`filter-cat-${option.value}`}
                            label={
                              <span>
                                {option.icon} {option.label} ({option.count})
                              </span>
                            }
                            checked={selectedCategories.has(option.value)}
                            onChange={() => handleCategoryToggle(option.value)}
                            className="me-3 small"
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>
                );
              })()}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Thread Table */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="m-0">🧵 Baseline + Active Thread Status ({filteredThreads.length})</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="small fw-bold">Status</th>
                    <th className="small fw-bold">Category</th>
                    <th className="small fw-bold">Thread Name</th>
                    <th className="small fw-bold">Baseline Key</th>
                    <th className="small fw-bold">ID</th>
                    <th className="small fw-bold">CPU %</th>
                    <th className="small fw-bold">Memory MB</th>
                    <th className="small fw-bold">Description</th>
                    <th className="small fw-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredThreads.map((thread, index) => {
                    const rowClassName = thread.status === 'missing' ? 'table-danger' : 
                                       thread.status === 'unexpected' ? 'table-warning' : 
                                       thread.status === 'duplicate' || thread.is_duplicate ? 'table-info' : '';
                    
                    return (
                      <tr key={`${thread.thread_id}-${thread.baseline_key || 'unknown'}-${index}`} className={rowClassName}>
                        <td>
                          {getStatusBadge(thread)}
                          {thread.is_duplicate && thread.status === 'active' && (
                            <Badge bg="info" className="ms-1 small">Primary</Badge>
                          )}
                        </td>
                        <td>
                          <span className="small">
                            {getCategoryIcon(thread.category)} {thread.category}
                          </span>
                        </td>
                        <td>
                          <code className="small">{thread.name}</code>
                          {!thread.is_baseline && (
                            <Badge bg="warning" className="ms-1 small">Unexpected</Badge>
                          )}
                          {thread.config?.required && thread.status === 'missing' && (
                            <Badge bg="danger" className="ms-1 small">Required</Badge>
                          )}
                        </td>
                        <td className="small">
                          {thread.baseline_key ? (
                            <code>{thread.baseline_key}</code>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="small">
                          {thread.status === 'missing' ? (
                            <span className="text-muted">-</span>
                          ) : (
                            thread.thread_id
                          )}
                        </td>
                        <td className="small">
                          {thread.status === 'active' ? `${thread.cpu_percent}%` : '-'}
                        </td>
                        <td className="small">
                          {thread.status === 'active' ? thread.memory_mb : '-'}
                        </td>
                        <td className="small text-muted">
                          {thread.config?.description || 'No description'}
                          {thread.config?.controllable && thread.status === 'active' && (
                            <Badge bg="info" className="ms-1 small">Controllable</Badge>
                          )}
                        </td>
                        <td>
                          {isThreadControllable(thread) && thread.status === 'active' ? (
                            <div className="d-flex gap-1">
                              <Button 
                                variant="outline-warning" 
                                size="sm" 
                                disabled={controlLoading}
                                onClick={() => handleThreadControl(thread, 'restart')}
                                title="Restart thread"
                              >
                                <i className="bi bi-arrow-clockwise"></i>
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                disabled={controlLoading}
                                onClick={() => handleThreadControl(thread, 'stop')}
                                title="Stop thread"
                              >
                                <i className="bi bi-stop-fill"></i>
                              </Button>
                            </div>
                          ) : (
                            <span className="small text-muted">
                              {thread.status === 'missing' ? 'Not running' : 
                               thread.status === 'unexpected' ? 'Unexpected' : 
                               'Not controllable'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {filteredThreads.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center py-3 text-muted">
                        No threads found for selected category
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Thread Information */}
      <Row className="mt-3">
        <Col lg={12}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="m-0">ℹ️ Thread Status Reference</h6>
            </Card.Header>
            <Card.Body className="py-2">
              <Row>
                <Col md={6}>
                  <div className="small">
                    <div className="mb-2"><strong>Thread Categories:</strong></div>
                    <div className="mb-1">• <strong>🔴 Critical Always:</strong> Core system threads</div>
                    <div className="mb-1">• <strong>⚙️ Application Services:</strong> Business logic threads</div>
                    <div className="mb-1">• <strong>🔧 System Threads:</strong> Python runtime threads</div>
                    <div className="mb-1">• <strong>🔍 Learned:</strong> Auto-discovered patterns</div>
                    <div className="mb-1">• <strong>⚠️ Unexpected:</strong> Not in baseline</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="small">
                    <div className="mb-2"><strong>Thread Status:</strong></div>
                    <div className="mb-1">• <Badge bg="success" className="small">Active</Badge> Running and in baseline</div>
                    <div className="mb-1">• <Badge bg="warning" className="small">Duplicate</Badge> Extra instances detected</div>
                    <div className="mb-1">• <Badge bg="danger" className="small">Missing</Badge> Expected but not running</div>
                    <div className="mb-1">• <Badge bg="warning" className="small">Unexpected</Badge> Running but not in baseline</div>
                    <div className="mb-1">• <Badge bg="info" className="small">Controllable</Badge> Can be managed</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Safety Confirmation Modal */}
      <Modal show={confirmationModal.show} onHide={() => setConfirmationModal({ show: false, thread: null, action: null, warnings: [] })}>
        <Modal.Header closeButton>
          <Modal.Title className="text-warning">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Thread Control Warning
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmationModal.thread && (
            <div>
              <p><strong>Thread:</strong> <code>{confirmationModal.thread.name}</code></p>
              <p><strong>Action:</strong> <Badge bg="warning">{confirmationModal.action}</Badge></p>
              
              <Alert variant="danger" className="mt-3">
                <Alert.Heading className="h6">⚠️ Safety Warnings:</Alert.Heading>
                <ul className="mb-0">
                  {confirmationModal.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
              
              <p className="text-muted small mt-3">
                Thread control operations may affect application stability. 
                Please confirm that you understand the risks and want to proceed.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setConfirmationModal({ show: false, thread: null, action: null, warnings: [] })}
            disabled={controlLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmedThreadControl}
            disabled={controlLoading}
          >
            {controlLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Executing...
              </>
            ) : (
              <>
                <i className="bi bi-exclamation-triangle me-1"></i>
                Proceed Anyway
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// Frontend Debug Tab Component
function FrontendDebugTab() {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});
  const loggerRef = useRef(null);
  const debugModulePromiseRef = useRef(null);

  const loadDebugLoggerModule = useCallback(() => {
    if (!debugModulePromiseRef.current) {
      debugModulePromiseRef.current = import('../utils/debugLogger');
    }
    return debugModulePromiseRef.current;
  }, []);

  const ensureLogger = useCallback(async () => {
    const debugModule = await loadDebugLoggerModule();
    if (!loggerRef.current) {
      loggerRef.current = debugModule.createLogger(
        'frontendDebugTab',
        'Admin debug tab state management',
        true,
        'INFO'
      );
    }
    return { debugModule, logger: loggerRef.current };
  }, [loadDebugLoggerModule]);

  const loadModules = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { debugModule, logger } = await ensureLogger();
      const settings = debugModule.getDebugSettings();
      logger.info('Loaded frontend debug settings', settings);
      logger.debug('Frontend debug modules data', settings.modules || {});
      setModules(settings.modules || {});
      return true;
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error loading frontend debug modules', err);
      }
      setError('Failed to load frontend debug modules');
      return false;
    } finally {
      setLoading(false);
    }
  }, [ensureLogger]);

  // Load frontend debug modules
  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleModuleToggle = async (moduleName, enabled) => {
    try {
      const { debugModule, logger } = await ensureLogger();
      const { enableModule, disableModule } = debugModule;

      if (enabled) {
        enableModule(moduleName);
      } else {
        disableModule(moduleName);
      }

      logger.info(
        `${enabled ? 'Enabled' : 'Disabled'} frontend debug module`,
        { moduleName }
      );

      const loaded = await loadModules();
      if (loaded) {
        setSuccessMessage(`${enabled ? 'Enabled' : 'Disabled'} ${moduleName} debugging`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error toggling frontend debug module', {
          moduleName,
          enabled,
          error: err
        });
      }
      setError(`Failed to ${enabled ? 'enable' : 'disable'} ${moduleName}`);
    }
  };

  const handleLevelChange = async (moduleName, level) => {
    try {
      const { debugModule, logger } = await ensureLogger();
      debugModule.setModuleLevel(moduleName, level);
      logger.info('Updated frontend debug module level', { moduleName, level });

      const loaded = await loadModules();
      if (loaded) {
        setSuccessMessage(`Updated ${moduleName} debug level to ${level}`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error changing frontend debug module level', {
          moduleName,
          level,
          error: err
        });
      }
      setError(`Failed to update ${moduleName} level`);
    }
  };

  const handleTimeoutChange = async (moduleName, timeoutMinutes) => {
    try {
      const { debugModule, logger } = await ensureLogger();
      const { setModuleTimeout } = debugModule;

      if (setModuleTimeout) {
        setModuleTimeout(moduleName, timeoutMinutes);
        logger.info('Updated frontend debug module timeout', {
          moduleName,
          timeoutMinutes
        });

        const loaded = await loadModules();
        if (loaded) {
          setSuccessMessage(`Updated ${moduleName} timeout to ${timeoutMinutes} minutes`);
          setTimeout(() => setSuccessMessage(''), 3000);
        }
      }
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error changing frontend debug module timeout', {
          moduleName,
          timeoutMinutes,
          error: err
        });
      }
      setError(`Failed to update ${moduleName} timeout`);
    }
  };

  const handleEnableAll = async () => {
    try {
      const { debugModule, logger } = await ensureLogger();
      debugModule.enableAllModules();
      logger.info('Enabled all frontend debug modules');

      const loaded = await loadModules();
      if (loaded) {
        setSuccessMessage('Enabled all frontend debug modules');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error enabling all frontend debug modules', err);
      }
      setError('Failed to enable all modules');
    }
  };

  const handleDisableAll = async () => {
    try {
      const { debugModule, logger } = await ensureLogger();
      debugModule.disableAllModules();
      logger.info('Disabled all frontend debug modules');

      const loaded = await loadModules();
      if (loaded) {
        setSuccessMessage('Disabled all frontend debug modules');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      if (loggerRef.current) {
        loggerRef.current.error('Error disabling all frontend debug modules', err);
      }
      setError('Failed to disable all modules');
    }
  };

  const toggleCardExpansion = (moduleName) => {
    setExpandedCards(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const expandAllCards = () => {
    const expanded = {};
    Object.keys(modules).forEach(name => expanded[name] = true);
    setExpandedCards(expanded);
  };

  const collapseAllCards = () => {
    setExpandedCards({});
  };

  // Calculate counts for filter buttons
  const allModules = Object.entries(modules);
  const activeModulesCount = allModules.filter(([, config]) => config.enabled).length;
  const inactiveModulesCount = allModules.filter(([, config]) => !config.enabled).length;

  // Filter modules based on search and status
  const filteredModules = Object.entries(modules).filter(([moduleName, config]) => {
    // First filter by status
    if (statusFilter === 'active' && !config.enabled) return false;
    if (statusFilter === 'inactive' && config.enabled) return false;

    // Then filter by search term
    if (!searchFilter) return true;
    const moduleKey = moduleName.toLowerCase();
    const searchTerm = searchFilter.toLowerCase();
    return moduleKey.includes(searchTerm);
  });

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading frontend debug modules...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
      {successMessage && <Alert variant="success" className="py-2 mb-3">{successMessage}</Alert>}

      {/* Controls Header */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-success" size="sm" onClick={handleEnableAll}>
              <i className="bi bi-check-circle me-1"></i>
              Enable All
            </Button>
            <Button variant="outline-warning" size="sm" onClick={handleDisableAll}>
              <i className="bi bi-x-circle me-1"></i>
              Disable All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={expandAllCards}>
              <i className="bi bi-arrows-expand me-1"></i>
              Expand All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={collapseAllCards}>
              <i className="bi bi-arrows-collapse me-1"></i>
              Collapse All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={loadModules}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      {/* Filter Buttons */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted me-2">Filter:</small>
            <ButtonGroup size="sm">
              <Button
                variant={statusFilter === 'all' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setStatusFilter('all');
                  setSearchFilter('');
                }}
              >
                All ({allModules.length})
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'success' : 'outline-success'}
                onClick={() => {
                  setStatusFilter('active');
                  setSearchFilter('');
                }}
              >
                Active ({activeModulesCount})
              </Button>
              <Button
                variant={statusFilter === 'inactive' ? 'secondary' : 'outline-secondary'}
                onClick={() => {
                  setStatusFilter('inactive');
                  setSearchFilter('');
                }}
              >
                Inactive ({inactiveModulesCount})
              </Button>
            </ButtonGroup>
          </div>
        </Col>
      </Row>

      {/* Search Filter */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup size="sm">
            <Form.Control
              type="text"
              placeholder="🔍 Search modules... (e.g., 'api', 'service', 'component')"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            {searchFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchFilter('')}
                title="Clear search"
                style={{ borderLeft: 'none' }}
              >
                <i className="bi bi-x"></i>
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>

      {/* Module Cards */}
      {filteredModules.length === 0 ? (
        <Row>
          <Col>
            <div className="text-center py-4">
              <div className="text-muted">
                <i className="bi bi-search fs-1 mb-3 d-block"></i>
                <h5>No modules found</h5>
                <p>
                  {searchFilter ?
                    <>No modules match "<strong>{searchFilter}</strong>". Try a different search term.</> :
                    'No frontend debug modules available.'
                  }
                </p>
                {searchFilter && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearchFilter('')}>
                    Clear Search
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      ) : (
        <Row>
          {filteredModules.map(([moduleName, config]) => (
          <Col lg={6} key={moduleName} className="mb-3">
            <Card className="h-100">
              <Card.Header
                className="py-2 cursor-pointer"
                onClick={() => toggleCardExpansion(moduleName)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2 text-decoration-none"
                      onClick={(e) => { e.stopPropagation(); toggleCardExpansion(moduleName); }}
                    >
                      <i className={`bi ${expandedCards[moduleName] ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
                    </Button>
                    <h6 className="m-0">
                      <i className={`bi ${config.enabled ? 'bi-bug-fill text-success' : 'bi-bug text-muted'} me-1`}></i> {moduleName}
                    </h6>
                  </div>
                  <div className="d-flex align-items-center">
                    {config.enabled && (
                      <Badge bg="success" className="me-2">
                        Active
                      </Badge>
                    )}
                    <Form.Check
                      type="switch"
                      id={`switch-${moduleName}`}
                      checked={config.enabled}
                      onChange={(e) => { e.stopPropagation(); handleModuleToggle(moduleName, e.target.checked); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              </Card.Header>

              <Collapse in={expandedCards[moduleName]}>
                <div>
                  <Card.Body className="py-2">
                    {config.description && (
                      <div className="small text-muted mb-2">{config.description}</div>
                    )}
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small fw-bold">Debug Level</Form.Label>
                          <Form.Select
                            size="sm"
                            value={['DEBUG', 'INFO', 'WARN', 'ERROR'][config.level] || 'INFO'}
                            onChange={(e) => handleLevelChange(moduleName, e.target.value)}
                            disabled={!config.enabled}
                          >
                            <option value="DEBUG">DEBUG</option>
                            <option value="INFO">INFO</option>
                            <option value="WARN">WARN</option>
                            <option value="ERROR">ERROR</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small fw-bold">Auto-timeout (minutes)</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max="1440"
                            value={config.timeout_minutes || 0}
                            onChange={(e) => handleTimeoutChange(moduleName, parseInt(e.target.value) || 0)}
                            disabled={!config.enabled}
                          />
                          <Form.Text className="text-muted small">0 = no timeout</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    {config.enabled && (
                      <div className="border-top pt-2 mt-2">
                        <small className="text-muted">
                          <div>Status: <Badge bg={config.enabled ? 'success' : 'secondary'} className="small">
                            {config.enabled ? 'Enabled' : 'Disabled'}
                          </Badge></div>
                          <div>Level: <code>{['DEBUG', 'INFO', 'WARN', 'ERROR'][config.level] || 'INFO'}</code></div>
                          {config.timeout_minutes > 0 && (
                            <div>Timeout: <code>{config.timeout_minutes} min</code></div>
                          )}
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </div>
              </Collapse>
            </Card>
          </Col>
          ))}
        </Row>
      )}

    </div>
  );
}

export default AdminSettings; 